import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, EyeOff, FolderInput, Pencil, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { AlertDialog } from '@/components/ui/AlertDialog';
import {
  createMenuItem, deleteMenuItem, getMenuAdmin, getMenuRoles, reorderMenu, setMenuItemRoles, updateMenuItem,
} from '@/api/menu';
import { MENU_QUERY_KEY } from '@/hooks/useMenu';
import { ICON_LIBRARY, iconFor } from '@/menu/icons';
import { cn } from '@/lib/cn';
import { isLeafKind, KIND_LABEL, type AdminMenuItem, type MenuKind, type MenuReorderEntry, type MenuRole } from '@/types/menu';
import { buildTree, kindsAllowedUnder, menuItemSchema, moveTargets, swapOrder, type MenuItemFormData, type TreeNode } from './schema';

const inputCls = 'rounded border border-border bg-white px-2 py-1.5 text-sm';
const btnPrimary =
  'inline-flex items-center gap-1.5 rounded bg-orange px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange';

const ADMIN_QUERY_KEY = ['menu', 'admin'] as const;
const ROLES_QUERY_KEY = ['menu', 'roles'] as const;

type Editor =
  | { mode: 'create'; parent: AdminMenuItem | null }
  | { mode: 'edit'; item: AdminMenuItem; hasChildren: boolean };

/**
 * Settings › Menu config — the oscar-admin editor for the sidebar (migration
 * 131; SPEC §9.1). Categories, sub-categories, pages and links; their order,
 * label, icon, and which Oscar roles see each one. Changes apply to everyone's
 * sidebar as soon as they save.
 *
 * Every write is admin-only and audited server-side (MenuAdminFunctions). A
 * menu entry is a LINK, not a permission: pages and the API keep their own
 * role checks whatever is shown here.
 */
export function MenuConfigPage() {
  const qc = useQueryClient();
  const menu = useQuery({ queryKey: ADMIN_QUERY_KEY, queryFn: ({ signal }) => getMenuAdmin(signal) });
  const roles = useQuery({ queryKey: ROLES_QUERY_KEY, queryFn: ({ signal }) => getMenuRoles(signal) });

  const [editor, setEditor] = useState<Editor | null>(null);
  const [access, setAccess] = useState<AdminMenuItem | null>(null);
  const [moving, setMoving] = useState<AdminMenuItem | null>(null);
  const [deleting, setDeleting] = useState<AdminMenuItem | null>(null);
  const [error, setError] = useState<string | null>(null);

  const items = useMemo(() => menu.data ?? [], [menu.data]);
  const tree = useMemo(() => buildTree(items), [items]);
  const roleName = useMemo(() => {
    const m = new Map((roles.data ?? []).map((r) => [r.RoleName, r.DisplayName]));
    return (r: string) => m.get(r) ?? r;
  }, [roles.data]);

  const refresh = () => {
    // Both the editor's tree and everybody's sidebar.
    void qc.invalidateQueries({ queryKey: MENU_QUERY_KEY });
  };
  const fail = (e: unknown) => setError(e instanceof Error ? e.message : 'The change could not be saved.');

  const reorder = useMutation({ mutationFn: (entries: MenuReorderEntry[]) => reorderMenu(entries), onSuccess: refresh, onError: fail });
  const remove = useMutation({
    mutationFn: (item: AdminMenuItem) => deleteMenuItem(item.MenuItemId, item.RowVersion),
    onSuccess: refresh,
    onError: fail,
  });

  const move = (node: TreeNode, siblings: TreeNode[], dir: -1 | 1) => {
    const entries = swapOrder(siblings, node.MenuItemId, dir);
    if (entries) { setError(null); reorder.mutate(entries); }
  };

  return (
    <section className="max-w-5xl">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">Menu config</h1>
          <p className="mt-1 max-w-3xl text-sm text-darkGrey">
            The sidebar: categories, sub-categories and the pages in them, in order, and who sees each one. Changes apply
            to everyone&rsquo;s sidebar when you save. Showing or hiding an item here changes the menu only &mdash; each
            page keeps its own access rules.
          </p>
        </div>
        <button type="button" className={btnPrimary} onClick={() => setEditor({ mode: 'create', parent: null })}>
          <Plus className="h-4 w-4" aria-hidden /> Add to top level
        </button>
      </header>

      {error && (
        <div role="alert" className="mb-3 flex items-start gap-3 rounded border border-red/40 bg-red/5 px-3 py-2 text-sm text-red">
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError(null)} className="text-xs font-semibold hover:underline">Dismiss</button>
        </div>
      )}

      <div className="rounded border border-border bg-white shadow-sm">
        {menu.isLoading && <p className="p-4 text-sm text-muted">Loading the menu…</p>}
        {menu.isError && (
          <p role="alert" className="p-4 text-sm text-red">
            Could not load the menu. If migration 131 has not been run yet, the sidebar is showing its built-in copy and
            there is nothing to edit until it is.
          </p>
        )}
        {menu.data && tree.length === 0 && (
          <p className="p-4 text-sm text-muted">The menu is empty. Add a category or a page to start.</p>
        )}
        {tree.length > 0 && (
          <ul aria-label="Menu items">
            <Rows
              nodes={tree}
              depth={0}
              roleName={roleName}
              busy={reorder.isPending}
              onMove={move}
              onAdd={(parent) => setEditor({ mode: 'create', parent })}
              onEdit={(item) => setEditor({ mode: 'edit', item, hasChildren: items.some((i) => i.ParentId === item.MenuItemId) })}
              onAccess={setAccess}
              onRelocate={setMoving}
              onDelete={setDeleting}
            />
          </ul>
        )}
      </div>

      <ItemDialog
        editor={editor}
        items={items}
        roles={roles.data ?? []}
        onClose={() => setEditor(null)}
        onSaved={refresh}
      />
      <AccessDialog item={access} roles={roles.data ?? []} onClose={() => setAccess(null)} onSaved={refresh} />
      <MoveDialog
        item={moving}
        items={items}
        busy={reorder.isPending}
        onClose={() => setMoving(null)}
        onMove={(item, parentId) => {
          const end = items.filter((i) => i.ParentId === parentId).reduce((m, i) => Math.max(m, i.SortOrder), 0) + 1;
          setError(null);
          reorder.mutate([{ MenuItemId: item.MenuItemId, ParentId: parentId, SortOrder: end }], { onSuccess: () => setMoving(null) });
        }}
      />
      <AlertDialog
        open={deleting !== null}
        onOpenChange={(o) => { if (!o) setDeleting(null); }}
        destructive
        title="Delete this menu item?"
        description={deleting
          ? `“${deleting.Title}” leaves the sidebar for everyone. The page itself is not affected. A category must be empty before it can be deleted.`
          : undefined}
        confirmLabel="Delete"
        onConfirm={() => { if (deleting) { setError(null); remove.mutate(deleting); } }}
      />
    </section>
  );
}

// ── Tree ──────────────────────────────────────────────────────────────────────

interface RowActions {
  roleName: (r: string) => string;
  busy: boolean;
  onMove: (node: TreeNode, siblings: TreeNode[], dir: -1 | 1) => void;
  onAdd: (parent: AdminMenuItem) => void;
  onEdit: (item: AdminMenuItem) => void;
  onAccess: (item: AdminMenuItem) => void;
  onRelocate: (item: AdminMenuItem) => void;
  onDelete: (item: AdminMenuItem) => void;
}

function Rows({ nodes, depth, ...a }: { nodes: TreeNode[]; depth: number } & RowActions) {
  return (
    <>
      {nodes.map((node, i) => {
        const Icon = iconFor(node.Icon);
        const canHoldChildren = kindsAllowedUnder(node.Kind).length > 0;
        return (
          <li key={node.MenuItemId} className="border-t border-border first:border-t-0">
            <div
              className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2', depth === 0 && !isLeafKind(node.Kind) && 'bg-bg')}
              style={{ paddingLeft: 12 + depth * 24 }}
            >
              <Icon className="h-4 w-4 shrink-0 text-navy" aria-hidden />
              <span className={cn('font-medium', node.IsActive ? 'text-navy' : 'text-muted line-through')}>{node.Title}</span>
              <span className="rounded bg-bg px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-darkGrey/80">
                {KIND_LABEL[node.Kind]}
              </span>
              {!node.IsActive && (
                <span className="inline-flex items-center gap-1 rounded bg-bg px-1.5 py-0.5 text-[11px] font-semibold text-darkGrey/80">
                  <EyeOff className="h-3 w-3" aria-hidden /> Hidden
                </span>
              )}
              {node.Url && <span className="truncate text-xs text-muted">{node.Url}</span>}
              <span className="inline-flex items-center gap-1 text-xs text-darkGrey/80">
                <ShieldCheck className="h-3 w-3" aria-hidden />
                {node.Roles.length === 0 ? 'Everyone signed in' : node.Roles.map(a.roleName).join(', ')}
              </span>

              <div className="ml-auto flex items-center gap-0.5">
                <IconButton label={`Move ${node.Title} up`} disabled={i === 0 || a.busy} onClick={() => a.onMove(node, nodes, -1)}>
                  <ChevronUp className="h-4 w-4" />
                </IconButton>
                <IconButton label={`Move ${node.Title} down`} disabled={i === nodes.length - 1 || a.busy} onClick={() => a.onMove(node, nodes, 1)}>
                  <ChevronDown className="h-4 w-4" />
                </IconButton>
                {node.Kind !== 'Menu' && (
                  <IconButton label={`Move ${node.Title} to…`} onClick={() => a.onRelocate(node)}>
                    <FolderInput className="h-4 w-4" />
                  </IconButton>
                )}
                <IconButton label={`Who can see ${node.Title}`} onClick={() => a.onAccess(node)}>
                  <ShieldCheck className="h-4 w-4" />
                </IconButton>
                {canHoldChildren && (
                  <IconButton label={`Add to ${node.Title}`} onClick={() => a.onAdd(node)}>
                    <Plus className="h-4 w-4" />
                  </IconButton>
                )}
                <IconButton label={`Edit ${node.Title}`} onClick={() => a.onEdit(node)}>
                  <Pencil className="h-4 w-4" />
                </IconButton>
                <IconButton label={`Delete ${node.Title}`} danger onClick={() => a.onDelete(node)}>
                  <Trash2 className="h-4 w-4" />
                </IconButton>
              </div>
            </div>
            {node.children.length > 0 && (
              <ul>
                <Rows nodes={node.children} depth={depth + 1} {...a} />
              </ul>
            )}
          </li>
        );
      })}
    </>
  );
}

function IconButton({ label, onClick, disabled, danger, children }: {
  label: string; onClick: () => void; disabled?: boolean; danger?: boolean; children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'rounded p-1.5 disabled:opacity-30 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy',
        danger ? 'text-red hover:bg-red/10' : 'text-darkGrey/70 hover:bg-bg hover:text-navy',
      )}
    >
      {children}
    </button>
  );
}

// ── Create / edit ─────────────────────────────────────────────────────────────

const emptyForm = (kind: MenuKind, roles: string[]): MenuItemFormData => ({
  Kind: kind, Title: '', Description: '', Icon: '', Url: '', Target: '_self', IsActive: true, Roles: roles,
});

function ItemDialog({ editor, items, roles, onClose, onSaved }: {
  editor: Editor | null;
  items: AdminMenuItem[];
  roles: MenuRole[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [submitError, setSubmitError] = useState<string | null>(null);

  const parent = editor?.mode === 'create'
    ? editor.parent
    : editor ? items.find((i) => i.MenuItemId === editor.item.ParentId) ?? null : null;
  const allowed = kindsAllowedUnder(parent?.Kind ?? null);
  // An item with children keeps its kind (the server refuses the change anyway).
  const kindLocked = editor?.mode === 'edit' && editor.hasChildren;

  const { register, handleSubmit, reset, watch, setValue, formState } = useForm<MenuItemFormData>({
    resolver: zodResolver(menuItemSchema),
    defaultValues: emptyForm('Route', []),
  });

  useEffect(() => {
    if (!editor) return;
    setSubmitError(null);
    if (editor.mode === 'edit') {
      const it = editor.item;
      reset({
        Kind: it.Kind, Title: it.Title, Description: it.Description ?? '', Icon: it.Icon ?? '', Url: it.Url ?? '',
        Target: it.Target, IsActive: it.IsActive, Roles: it.Roles,
      });
    } else {
      // A new item starts with its category's audience, so adding a page under
      // Admin does not quietly show it to everybody.
      reset(emptyForm(editor.parent ? 'Route' : 'Menu', editor.parent?.Roles ?? []));
    }
  }, [editor, reset]);

  const kind = watch('Kind');
  const icon = watch('Icon');
  const chosenRoles = watch('Roles');

  const save = useMutation({
    mutationFn: (d: MenuItemFormData) => {
      const leaf = isLeafKind(d.Kind);
      const body = {
        Kind: d.Kind,
        Title: d.Title.trim(),
        Description: d.Description.trim() || null,
        Icon: d.Icon || null,
        Url: leaf ? d.Url.trim() : null,
        Target: d.Kind === 'Report' ? d.Target : ('_self' as const),
        IsActive: d.IsActive,
      };
      if (editor?.mode === 'edit')
        return updateMenuItem(editor.item.MenuItemId, { ...body, RowVersion: editor.item.RowVersion });
      return createMenuItem({ ...body, ParentId: parent?.MenuItemId ?? null, Roles: d.Roles });
    },
    onSuccess: () => { onSaved(); onClose(); },
    onError: (e) => setSubmitError(e instanceof Error ? e.message : 'The item could not be saved.'),
  });

  const submit = () => void handleSubmit((d) => save.mutate(d))();
  const title = editor?.mode === 'edit'
    ? `Edit “${editor.item.Title}”`
    : parent ? `Add to “${parent.Title}”` : 'Add to the top level';

  return (
    <AlertDialog
      open={editor !== null}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={title}
      wide
      confirmLabel={save.isPending ? 'Saving…' : 'Save'}
      confirmDisabled={save.isPending}
      closeOnConfirm={false}
      onConfirm={submit}
    >
      <form className="grid gap-3 sm:grid-cols-2" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <label className="block text-sm font-semibold text-darkGrey">
          Type
          <select {...register('Kind')} className={`${inputCls} mt-1 w-full font-normal`}>
            {(kindLocked && editor?.mode === 'edit' ? [editor.item.Kind] : allowed).map((k) => (
              <option key={k} value={k}>{KIND_LABEL[k]}</option>
            ))}
          </select>
          {kindLocked && <span className="mt-1 block text-xs font-normal text-muted">It holds items, so it stays a {KIND_LABEL[kind].toLowerCase()}.</span>}
        </label>

        <label className="block text-sm font-semibold text-darkGrey">
          Label
          <input {...register('Title')} className={`${inputCls} mt-1 w-full font-normal`} autoComplete="off" />
          {formState.errors.Title && <FieldError message={formState.errors.Title.message} />}
        </label>

        {kind === 'Route' && (
          <label className="block text-sm font-semibold text-darkGrey sm:col-span-2">
            App path
            <input {...register('Url')} placeholder="/assets" className={`${inputCls} mt-1 w-full font-normal`} autoComplete="off" />
            <span className="mt-1 block text-xs font-normal text-muted">
              A page inside Oscar, starting with “/”. The page must already exist — the menu links to pages, it
              doesn&rsquo;t create them.
            </span>
            {formState.errors.Url && <FieldError message={formState.errors.Url.message} />}
          </label>
        )}

        {kind === 'Report' && (
          <>
            <label className="block text-sm font-semibold text-darkGrey">
              URL
              <input {...register('Url')} placeholder="https://…" className={`${inputCls} mt-1 w-full font-normal`} autoComplete="off" />
              {formState.errors.Url && <FieldError message={formState.errors.Url.message} />}
            </label>
            <label className="block text-sm font-semibold text-darkGrey">
              Opens in
              <select {...register('Target')} className={`${inputCls} mt-1 w-full font-normal`}>
                <option value="_blank">A new tab</option>
                <option value="_self">This tab</option>
              </select>
            </label>
          </>
        )}

        <label className="block text-sm font-semibold text-darkGrey sm:col-span-2">
          Tooltip <span className="font-normal text-muted">(optional)</span>
          <input {...register('Description')} className={`${inputCls} mt-1 w-full font-normal`} autoComplete="off" />
          {formState.errors.Description && <FieldError message={formState.errors.Description.message} />}
        </label>

        <fieldset className="sm:col-span-2">
          <legend className="text-sm font-semibold text-darkGrey">Icon</legend>
          <div className="mt-1 grid max-h-40 grid-cols-8 gap-1 overflow-y-auto rounded border border-border p-1 sm:grid-cols-12" role="radiogroup" aria-label="Icon">
            {ICON_LIBRARY.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={icon === key}
                aria-label={label}
                title={label}
                onClick={() => setValue('Icon', icon === key ? '' : key, { shouldDirty: true })}
                className={cn('flex items-center justify-center rounded p-1.5', icon === key ? 'bg-navy text-white' : 'text-navy hover:bg-bg')}
              >
                <Icon className="h-4 w-4" aria-hidden />
              </button>
            ))}
          </div>
        </fieldset>

        {editor?.mode === 'create' && (
          <fieldset className="sm:col-span-2">
            <legend className="text-sm font-semibold text-darkGrey">Who can see it</legend>
            <RoleChecks
              roles={roles}
              selected={chosenRoles}
              onChange={(next) => setValue('Roles', next, { shouldDirty: true })}
            />
          </fieldset>
        )}

        <label className="flex items-center gap-2 text-sm text-darkGrey sm:col-span-2">
          <input type="checkbox" {...register('IsActive')} /> Show in the sidebar
        </label>

        {submitError && <p role="alert" className="text-sm text-red sm:col-span-2">{submitError}</p>}
      </form>
    </AlertDialog>
  );
}

function FieldError({ message }: { message?: string }) {
  return <span className="mt-1 block text-xs font-normal text-red">{message}</span>;
}

function RoleChecks({ roles, selected, onChange }: {
  roles: MenuRole[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const toggle = (r: string) => onChange(selected.includes(r) ? selected.filter((x) => x !== r) : [...selected, r]);
  return (
    <div className="mt-1 space-y-1">
      {roles.length === 0 && <p className="text-sm text-muted">No roles are catalogued.</p>}
      {roles.map((r) => (
        <label key={r.RoleName} className="flex items-center gap-2 text-sm text-darkGrey">
          <input type="checkbox" checked={selected.includes(r.RoleName)} onChange={() => toggle(r.RoleName)} />
          <span className="font-medium">{r.DisplayName}</span>
          <span className="text-xs text-muted">{r.RoleName}</span>
        </label>
      ))}
      <p className="pt-1 text-xs text-muted">
        {selected.length === 0
          ? 'None ticked: everyone signed in to Oscar sees it.'
          : 'Only people holding one of the ticked roles see it. A category is hidden when none of its items are visible.'}
      </p>
    </div>
  );
}

// ── Who can see ───────────────────────────────────────────────────────────────

function AccessDialog({ item, roles, onClose, onSaved }: {
  item: AdminMenuItem | null;
  roles: MenuRole[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  useEffect(() => { if (item) { setSelected(item.Roles); setSubmitError(null); } }, [item]);

  const save = useMutation({
    mutationFn: () => setMenuItemRoles(item!.MenuItemId, item!.RowVersion, selected),
    onSuccess: () => { onSaved(); onClose(); },
    onError: (e) => setSubmitError(e instanceof Error ? e.message : 'Could not save who can see it.'),
  });

  return (
    <AlertDialog
      open={item !== null}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={item ? `Who can see “${item.Title}”?` : ''}
      description="This changes the menu only. The page keeps its own access rules."
      confirmLabel={save.isPending ? 'Saving…' : 'Save'}
      confirmDisabled={save.isPending}
      closeOnConfirm={false}
      onConfirm={() => save.mutate()}
    >
      <RoleChecks roles={roles} selected={selected} onChange={setSelected} />
      {submitError && <p role="alert" className="mt-2 text-sm text-red">{submitError}</p>}
    </AlertDialog>
  );
}

// ── Move to ───────────────────────────────────────────────────────────────────

function MoveDialog({ item, items, busy, onClose, onMove }: {
  item: AdminMenuItem | null;
  items: AdminMenuItem[];
  busy: boolean;
  onClose: () => void;
  onMove: (item: AdminMenuItem, parentId: number | null) => void;
}) {
  const [choice, setChoice] = useState<string>('');
  useEffect(() => { setChoice(''); }, [item]);
  const targets = item ? moveTargets(item, items) : [];
  const label = (t: AdminMenuItem | null) => {
    if (!t) return 'Top level';
    const p = items.find((i) => i.MenuItemId === t.ParentId);
    return p ? `${p.Title} › ${t.Title}` : t.Title;
  };

  return (
    <AlertDialog
      open={item !== null}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={item ? `Move “${item.Title}”` : ''}
      description="It goes to the end of the list you choose."
      confirmLabel={busy ? 'Moving…' : 'Move'}
      confirmDisabled={busy || choice === ''}
      closeOnConfirm={false}
      onConfirm={() => { if (item && choice !== '') onMove(item, choice === 'top' ? null : Number(choice)); }}
    >
      {targets.length === 0 ? (
        <p className="text-sm text-muted">There is nowhere else this can go.</p>
      ) : (
        <label className="block text-sm font-semibold text-darkGrey">
          Into
          <select value={choice} onChange={(e) => setChoice(e.target.value)} className={`${inputCls} mt-1 w-full font-normal`}>
            <option value="">Choose…</option>
            {targets.map((t) => (
              <option key={t?.MenuItemId ?? 'top'} value={t ? String(t.MenuItemId) : 'top'}>
                {label(t)}{t ? ` (${KIND_LABEL[t.Kind].toLowerCase()})` : ''}
              </option>
            ))}
          </select>
        </label>
      )}
    </AlertDialog>
  );
}
